import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiLoader } from "react-icons/fi";
import {
  Mail,
  Lock,
  LogIn,
  Shield,
  AlertCircle,
  Eye,
  EyeOff,
} from "lucide-react";

const ResidenceAdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        "https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/ResidenceManager/login",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            Username: email,
            Password: password,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        
        sessionStorage.setItem("residenceManager", JSON.stringify(data));
        sessionStorage.setItem("userRole", "residence_admin");
        
        if (data.token) {
          sessionStorage.setItem("authToken", data.token);
        }
        
        if (data.userId) {
          sessionStorage.setItem("userId", data.userId);
        }
        
        sessionStorage.setItem("loginTime", Date.now().toString());
        
        navigate("/ResidenceAdminDashboard");
      } else if (response.status === 401) {
        setError("Invalid email or password");
      } else {
        const message = await response.text();
        setError(message || "Login failed");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 sm:px-6 py-8 relative"
      style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1523240795612-9a054b0db644?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      {/* Darker overlay for better readability */}
      <div className="absolute inset-0 bg-black/60" />

      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-6 sm:p-8">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 rounded-xl bg-white border border-gray-200 shadow-md overflow-hidden p-1.5">
              <img
                src="/LOGOSTAY.png"
                alt="MSA Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <h1 className="mt-4 text-xl font-bold text-gray-900">
              Residence Admin
            </h1>
            <p className="text-sm text-gray-500 mt-1 text-center">
              Sign in to manage residence operations
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@msa.edu"
                  required
                  className="
                    w-full pl-10 pr-4 py-2.5 text-sm
                    border border-gray-300 rounded-lg
                    text-gray-900 placeholder:text-gray-400
                    focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500
                    transition-all duration-200
                  "
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="
                    w-full pl-10 pr-10 py-2.5 text-sm
                    border border-gray-300 rounded-lg
                    text-gray-900 placeholder:text-gray-400
                    focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500
                    transition-all duration-200
                  "
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg border border-red-100">
                <AlertCircle className="w-4 h-4 text-red-500" />
                <p className="text-red-600 text-sm font-medium flex-1">
                  {error}
                </p>
              </div>
            )}

            {/* Login Button */}
            <button
              type="submit"
              disabled={loading}
              className={`
                w-full flex items-center justify-center gap-2
                px-4 py-2.5 rounded-lg text-sm font-medium
                transition-all duration-200
                ${loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-gray-800 hover:bg-gray-700 shadow-sm"}
                text-white
              `}
            >
              {loading ? (
                <>
                  <FiLoader className="animate-spin w-4 h-4" />
                  <span>Logging in...</span>
                </>
              ) : (
                <>
                  <LogIn className="w-4 h-4" />
                  <span>Login</span>
                </>
              )}
            </button>

            {/* Support Message */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-start gap-3">
                <div className="p-1.5 bg-gray-100 rounded-lg">
                  <Shield className="w-4 h-4 text-gray-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-xs font-semibold text-gray-900 mb-1">
                    Need help with your password?
                  </h4>
                  <p className="text-xs text-gray-600">
                    Please contact MSA Support at{" "}
                    <a 
                      href="mailto:support@msa.edu" 
                      className="font-semibold text-gray-800 hover:underline"
                    >
                      support@msastay.com
                    </a>{" "}
                    or call{" "}
                    <span className="font-semibold text-gray-800">+27 11 123 4567</span>
                  </p>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-white/80">
            © 2024 MSA Residence Management. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResidenceAdminLogin;